import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Xmark, RefreshDouble } from 'iconoir-react';
import { CalendarDays, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { getApiErrorDetail } from 'utils/api-error';
import {
  usePauseScheduledJob,
  useResumeScheduledJob,
  useRunScheduledJobNow,
  useSchedulePreview,
  useScheduledJobRuns,
  useUpdateScheduledJob,
} from '../hooks/use-scheduled-jobs';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';
import {
  builderStateFromSpec,
  buildScheduleSpec,
  type ScheduledJobBuilderState,
  type ScheduledJobEndRepeat,
  type ScheduledJobFrequency,
} from '../utils/scheduled-job-schedule';
import {
  SCHEDULE_DISPLAY_TIME_ZONE,
  formatInstantLabel,
  formatRunTimestamp,
  parseIsoInstant,
  resolveScheduleNextRunDate,
} from '../utils/scheduled-job-time';

type EditScheduledJobModalProps = {
  open: boolean;
  job: ScheduledJobItem | null;
  onOpenChange: (open: boolean) => void;
  onOpenChat?: (job: ScheduledJobItem) => void;
  onPromptUpdated?: (job: ScheduledJobItem, prompt: string, hilEnabled: boolean) => void;
};

const TIME_OPTIONS = [
  '6:00 AM',
  '8:00 AM',
  '9:00 AM',
  '12:00 PM',
  '2:00 PM',
  '5:00 PM',
  '8:00 PM',
  '9:00 PM',
] as const;

const FREQUENCY_SELECT_OPTIONS: { value: ScheduledJobFrequency; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom cron' },
];

const END_REPEAT_SELECT_OPTIONS: { value: ScheduledJobEndRepeat; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: '7d', label: 'After 7 days' },
  { value: '30d', label: 'After 30 days' },
  { value: 'onDate', label: 'On date' },
];

const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const RUN_STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  succeeded: 'default',
  failed: 'destructive',
  timeout: 'destructive',
  running: 'secondary',
  skipped_overlap: 'outline',
  skipped_misfire: 'outline',
  dropped: 'outline',
};

function to12HourLabel(hhmm: string): string {
  const parsed = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!parsed) return hhmm;
  const hour = parseInt(parsed[1], 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${parsed[2]} ${period}`;
}

function from12HourLabel(label: string): string {
  const parsed = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(label.trim());
  if (!parsed) return '09:00';
  let hour = parseInt(parsed[1], 10) % 12;
  if (/pm/i.test(parsed[3])) hour += 12;
  return `${String(hour).padStart(2, '0')}:${parsed[2]}`;
}

const localRunDate = (job?: Pick<ScheduledJobItem, 'nextRunAtLocal' | 'nextRunAt'> | null) =>
  resolveScheduleNextRunDate(job?.nextRunAtLocal, job?.nextRunAt);

const formatNextRunLabel = (job?: Pick<ScheduledJobItem, 'nextRunAtLocal' | 'nextRunAt'> | null): string => {
  const date = localRunDate(job);
  if (!date) return '—';
  try {
    return formatInstantLabel(date, SCHEDULE_DISPLAY_TIME_ZONE);
  } catch {
    return '—';
  }
};

function FrequencyRow({
  label,
  children,
  showDivider = false,
}: {
  label: string;
  children: React.ReactNode;
  showDivider?: boolean;
}) {
  return (
    <>
      {showDivider ? <div className="h-px w-full bg-grey-100 dark:bg-border" /> : null}
      <div className="flex w-full items-center justify-between gap-3">
        <span className="shrink-0 text-sm font-normal leading-[1.2] text-grey-900 dark:text-foreground">
          {label}
        </span>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </>
  );
}

function SelectValueTrigger({
  value,
  onValueChange,
  options,
  ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly string[];
  ariaLabel: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className="h-auto w-full justify-end gap-1 border-0 bg-transparent p-0 text-xs font-normal leading-[1.2] text-grey-500 shadow-none focus:ring-0 focus:ring-offset-0 dark:text-muted-foreground [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:opacity-100 [&>svg]:text-grey-500 dark:[&>svg]:text-muted-foreground"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        align="end"
        position="item-aligned"
        className="w-max min-w-[7.5rem] max-w-[10rem]"
      >
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function KeyedSelectTrigger<TValue extends string>({
  value,
  onValueChange,
  options,
  ariaLabel,
}: {
  value: TValue;
  onValueChange: (value: TValue) => void;
  options: { value: TValue; label: string }[];
  ariaLabel: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as TValue)}>
      <SelectTrigger
        aria-label={ariaLabel}
        className="h-auto w-full justify-end gap-1 border-0 bg-transparent p-0 text-xs font-normal leading-[1.2] text-grey-500 shadow-none focus:ring-0 focus:ring-offset-0 dark:text-muted-foreground [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:opacity-100 [&>svg]:text-grey-500 dark:[&>svg]:text-muted-foreground"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" position="item-aligned" className="w-max min-w-[7.5rem] max-w-[12rem]">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function WeekdayToggleRow({
  weekdays,
  onToggle,
}: {
  weekdays: number[];
  onToggle: (day: number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {WEEKDAY_INDEXES.map((day) => (
        <button
          key={day}
          type="button"
          onClick={() => onToggle(day)}
          className={`min-w-9 rounded-full px-2 py-1 text-xs font-medium transition-colors ${
            weekdays.includes(day)
              ? 'bg-black-base text-true-white dark:bg-primary-foreground dark:text-black-base'
              : 'bg-true-white text-grey-500 dark:bg-card dark:text-muted-foreground'
          } border border-grey-100 dark:border-border`}
        >
          {WEEKDAY_LABELS[day]}
        </button>
      ))}
    </div>
  );
}

function useMuiPickerTheme() {
  const isDark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  return useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: 'hsl(var(--foreground))',
            contrastText: 'hsl(var(--background))',
          },
          background: {
            default: 'hsl(var(--background))',
            paper: 'hsl(var(--card))',
          },
          text: {
            primary: 'hsl(var(--foreground))',
            secondary: 'hsl(var(--muted-foreground))',
          },
          divider: 'hsl(var(--border))',
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          allVariants: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
        components: {
          MuiPickersLayout: {
            styleOverrides: {
              root: {
                backgroundColor: 'transparent',
                fontFamily: "'Poppins', sans-serif",
              },
            },
          },
          MuiPickersToolbar: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                padding: '6px 14px',
              },
            },
          },
          MuiTimePickerToolbar: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
              },
              hourMinuteLabel: {
                fontFamily: "'Poppins', sans-serif",
                '& .MuiTypography-root': {
                  fontFamily: "'Poppins', sans-serif",
                },
              },
              ampmSelection: {
                fontFamily: "'Poppins', sans-serif",
                '& .MuiTypography-root': {
                  fontFamily: "'Poppins', sans-serif",
                },
              },
            },
          },
          MuiClock: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: 'transparent',
              },
              clock: {
                backgroundColor: 'hsl(var(--muted))',
              },
              wrapper: {
                backgroundColor: 'transparent',
              },
            },
          },
          MuiClockNumber: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                fontSize: '0.85rem',
                fontWeight: 500,
                zIndex: 2,
                userSelect: 'none',
                color: 'hsl(var(--foreground))',
                '&.Mui-selected': {
                  fontWeight: 700,
                  color: 'hsl(var(--foreground))',
                  backgroundColor: 'transparent',
                },
              },
            },
          },
          MuiClockPointer: {
            styleOverrides: {
              root: {
                backgroundColor: 'hsl(var(--foreground))',
                zIndex: 1,
              },
              thumb: {
                borderColor: 'hsl(var(--foreground))',
                backgroundColor: 'hsl(var(--foreground))',
                opacity: 0.35,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                fontFamily: "'Poppins', sans-serif",
                textTransform: 'none',
                fontWeight: 500,
              },
            },
          },
        },
      }),
    [isDark]
  );
}

function parseDateYmd(ymd: string): Date | undefined {
  if (!ymd) return undefined;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function formatDateYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatRunAtDisplay(runAt: string): string {
  if (!runAt) return '';
  const [datePart, timePart = '09:00'] = runAt.split('T');
  const parsed = parseDateYmd(datePart);
  if (!parsed) return runAt;
  const dateFormatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
  return `${dateFormatted}, ${to12HourLabel(timePart)}`;
}

function formatEndDateDisplay(endDate: string): string {
  if (!endDate) return '';
  const parsed = parseDateYmd(endDate);
  if (!parsed) return endDate;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

const SPATIAL_CALENDAR_CLASS_NAMES = {
  head_cell:
    'before:!content-none after:!content-none text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
  cell:
    'before:!content-none after:!content-none h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
  day:
    'h-9 w-9 p-0 font-normal text-foreground aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors',
};

function DateTimePickerTrigger({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value || '');
  const initialValueRef = useRef(value || '');
  const muiTheme = useMuiPickerTheme();

  useEffect(() => {
    if (open) {
      initialValueRef.current = value || '';
      setDraftValue(value || '');
    }
  }, [open, value]);

  const [datePart = '', timePart = '09:00'] = (draftValue || '').split('T');
  const selectedDate = useMemo(() => parseDateYmd(datePart), [datePart]);
  const currentTime = timePart || '09:00';
  const displayLabel = useMemo(() => formatRunAtDisplay(value), [value]);
  const draftDisplayLabel = useMemo(() => formatRunAtDisplay(draftValue), [draftValue]);

  const timeAsDate = useMemo(() => {
    const [hStr = '09', mStr = '00'] = currentTime.split(':');
    const d = new Date();
    d.setHours(parseInt(hStr, 10), parseInt(mStr, 10), 0, 0);
    return d;
  }, [currentTime]);

  const handleDateSelect = (date?: Date) => {
    if (!date) return;
    const newDateStr = formatDateYmd(date);
    setDraftValue(`${newDateStr}T${currentTime}`);
  };

  const handleTimeChange = (newTime: string) => {
    const activeDateStr = datePart || formatDateYmd(new Date());
    setDraftValue(`${activeDateStr}T${newTime || '09:00'}`);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
    } else {
      initialValueRef.current = value || '';
      setDraftValue(value || '');
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setDraftValue(initialValueRef.current);
    setOpen(false);
  };

  const handleOk = () => {
    const committed = draftValue || value || `${formatDateYmd(new Date())}T09:00`;
    onChange(committed);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="flex h-auto w-full items-center justify-end gap-1.5 border-0 bg-transparent p-0 text-xs font-normal leading-[1.2] text-grey-500 hover:text-grey-900 shadow-none focus:outline-none dark:text-muted-foreground dark:hover:text-foreground"
        >
          <CalendarDays className="size-4 shrink-0 text-grey-500 dark:text-muted-foreground" />
          <span>{displayLabel || t('SELECT_DATE_TIME', { defaultValue: 'Select date & time' })}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto overflow-hidden rounded-xl border-border p-0 shadow-2xl bg-card"
      >
        <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
          <div className="text-xs font-semibold text-foreground">
            {draftDisplayLabel || t('SELECT_DATE_TIME', { defaultValue: 'Select date & time' })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center sm:items-stretch divide-y sm:divide-y-0 sm:divide-x divide-border">
          <div className="p-3 flex flex-col items-center">
            <div className="w-full pb-1 px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
              <CalendarDays className="size-3.5" />
              <span>{t('SELECT_DATE', { defaultValue: 'Select date' })}</span>
            </div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              className="p-1"
              classNames={SPATIAL_CALENDAR_CLASS_NAMES}
            />
          </div>

          <div className="p-3 flex flex-col items-center sm:items-stretch justify-start">
            <div className="w-full pb-1 px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
              <Clock className="size-3.5" />
              <span>{t('SELECT_TIME', { defaultValue: 'Select time' })}</span>
            </div>
            <MuiThemeProvider theme={muiTheme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StaticTimePicker
                  value={timeAsDate}
                  onChange={(newDate) => {
                    if (newDate && !isNaN(newDate.getTime())) {
                      const hh = String(newDate.getHours()).padStart(2, '0');
                      const mm = String(newDate.getMinutes()).padStart(2, '0');
                      handleTimeChange(`${hh}:${mm}`);
                    }
                  }}
                  onAccept={() => handleOk()}
                  onClose={() => setOpen(false)}
                  ampm
                  localeText={{
                    toolbarTitle: '',
                  }}
                  slotProps={{
                    actionBar: {
                      actions: [],
                    },
                  }}
                  sx={{
                    bgcolor: 'transparent',
                    fontFamily: "'Poppins', sans-serif",
                    '& .MuiPickersLayout-root': {
                      bgcolor: 'transparent',
                    },
                    '& .MuiPickersLayout-contentWrapper': {
                      bgcolor: 'transparent',
                    },
                    '& .MuiTypography-root': {
                      fontFamily: "'Poppins', sans-serif",
                    },
                    '& .MuiPickersToolbar-root .MuiTypography-overline': {
                      display: 'none',
                    },
                    '& .MuiClock-root': {
                      backgroundColor: 'transparent !important',
                      bgcolor: 'transparent !important',
                    },
                    '& .MuiClock-clock': {
                      backgroundColor: 'hsl(var(--muted)) !important',
                      bgcolor: 'hsl(var(--muted)) !important',
                    },
                    '& .MuiClockPointer-root': {
                      zIndex: 1,
                    },
                    '& .MuiClockPointer-thumb': {
                      opacity: '0.35 !important',
                    },
                    '& .MuiClockNumber-root': {
                      zIndex: '2 !important',
                      color: 'hsl(var(--foreground)) !important',
                      userSelect: 'none',
                    },
                    '& .MuiClockNumber-root.Mui-selected': {
                      fontWeight: '700 !important',
                      color: 'hsl(var(--foreground)) !important',
                      backgroundColor: 'transparent !important',
                    },
                  }}
                />
              </LocalizationProvider>
            </MuiThemeProvider>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 bg-transparent">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="h-8 rounded-full border-border bg-background px-4 text-xs font-medium text-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
          >
            {t('CANCEL', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleOk}
            className="h-8 rounded-full bg-black-base px-4 text-xs font-medium text-true-white shadow-none hover:bg-grey-900 dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white"
          >
            {t('OK', { defaultValue: 'OK' })}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DatePickerTrigger({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedDate = useMemo(() => parseDateYmd(value), [value]);
  const displayLabel = useMemo(() => formatEndDateDisplay(value), [value]);

  const handleDateSelect = (date?: Date) => {
    if (!date) return;
    onChange(formatDateYmd(date));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="flex h-auto w-full items-center justify-end gap-1.5 border-0 bg-transparent p-0 text-xs font-normal leading-[1.2] text-grey-500 hover:text-grey-900 shadow-none focus:outline-none dark:text-muted-foreground dark:hover:text-foreground"
        >
          <CalendarDays className="size-3.5 shrink-0 text-grey-500 dark:text-muted-foreground" />
          <span>{displayLabel || t('SELECT_DATE', { defaultValue: 'Select date' })}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto rounded-xl border-border p-0 shadow-lg bg-card">
        <div className="border-b border-border px-4 py-3">
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
          <div className="mt-1 text-sm font-medium text-foreground">
            {displayLabel || t('SELECT_DATE', { defaultValue: 'Select date' })}
          </div>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          initialFocus
          className="p-3.5"
          classNames={SPATIAL_CALENDAR_CLASS_NAMES}
        />
      </PopoverContent>
    </Popover>
  );
}

function TimePickerTrigger({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value || '09:00');
  const initialValueRef = useRef(value || '09:00');
  const muiTheme = useMuiPickerTheme();

  useEffect(() => {
    if (open) {
      initialValueRef.current = value || '09:00';
      setDraftValue(value || '09:00');
    }
  }, [open, value]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
    } else {
      initialValueRef.current = value || '09:00';
      setDraftValue(value || '09:00');
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setDraftValue(initialValueRef.current);
    setOpen(false);
  };

  const handleOk = () => {
    onChange(draftValue || '09:00');
    setOpen(false);
  };

  const displayLabel = to12HourLabel(value || '09:00');
  const draftDisplayLabel = to12HourLabel(draftValue || '09:00');

  const timeAsDate = useMemo(() => {
    const [hStr = '09', mStr = '00'] = (draftValue || '09:00').split(':');
    const d = new Date();
    d.setHours(parseInt(hStr, 10), parseInt(mStr, 10), 0, 0);
    return d;
  }, [draftValue]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="flex h-auto w-full items-center justify-end gap-1.5 border-0 bg-transparent p-0 text-xs font-normal leading-[1.2] text-grey-500 hover:text-grey-900 shadow-none focus:outline-none dark:text-muted-foreground dark:hover:text-foreground"
        >
          <Clock className="size-3.5 shrink-0 text-grey-500 dark:text-muted-foreground" />
          <span>{displayLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto overflow-hidden rounded-xl border-border p-0 shadow-xl bg-card">
        <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
          <div className="text-xs font-medium text-muted-foreground">{label}</div>
          <div className="text-xs font-semibold text-foreground">{draftDisplayLabel}</div>
        </div>
        <div className="p-3 flex flex-col justify-start">
          <div className="w-full pb-1 px-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
            <Clock className="size-3.5" />
            <span>{t('SELECT_TIME', { defaultValue: 'Select time' })}</span>
          </div>
          <MuiThemeProvider theme={muiTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticTimePicker
                value={timeAsDate}
                onChange={(newDate) => {
                  if (newDate && !isNaN(newDate.getTime())) {
                    const hh = String(newDate.getHours()).padStart(2, '0');
                    const mm = String(newDate.getMinutes()).padStart(2, '0');
                    setDraftValue(`${hh}:${mm}`);
                  }
                }}
                onAccept={() => handleOk()}
                onClose={() => setOpen(false)}
                ampm
                localeText={{
                  toolbarTitle: '',
                }}
                slotProps={{
                  actionBar: {
                    actions: [],
                  },
                }}
                sx={{
                  bgcolor: 'transparent',
                  fontFamily: "'Poppins', sans-serif",
                  '& .MuiPickersLayout-root': {
                    bgcolor: 'transparent',
                  },
                  '& .MuiPickersLayout-contentWrapper': {
                    bgcolor: 'transparent',
                  },
                  '& .MuiTypography-root': {
                    fontFamily: "'Poppins', sans-serif",
                  },
                  '& .MuiPickersToolbar-root .MuiTypography-overline': {
                    display: 'none',
                  },
                  '& .MuiClock-root': {
                    backgroundColor: 'transparent !important',
                    bgcolor: 'transparent !important',
                  },
                  '& .MuiClock-clock': {
                    backgroundColor: 'hsl(var(--muted)) !important',
                    bgcolor: 'hsl(var(--muted)) !important',
                  },
                  '& .MuiClockPointer-root': {
                    zIndex: 1,
                  },
                  '& .MuiClockPointer-thumb': {
                    opacity: '0.35 !important',
                  },
                  '& .MuiClockNumber-root': {
                    zIndex: '2 !important',
                    color: 'hsl(var(--foreground)) !important',
                    userSelect: 'none',
                  },
                  '& .MuiClockNumber-root.Mui-selected': {
                    fontWeight: '700 !important',
                    color: 'hsl(var(--foreground)) !important',
                    backgroundColor: 'transparent !important',
                  },
                }}
              />
            </LocalizationProvider>
          </MuiThemeProvider>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 bg-transparent">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="h-8 rounded-full border-border bg-background px-4 text-xs font-medium text-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
          >
            {t('CANCEL', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleOk}
            className="h-8 rounded-full bg-black-base px-4 text-xs font-medium text-true-white shadow-none hover:bg-grey-900 dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white"
          >
            {t('OK', { defaultValue: 'OK' })}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function EditScheduledJobModal({
  open,
  job,
  onOpenChange,
  onOpenChat,
  onPromptUpdated,
}: EditScheduledJobModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateMutation = useUpdateScheduledJob();
  const pauseMutation = usePauseScheduledJob();
  const resumeMutation = useResumeScheduledJob();
  const runNowMutation = useRunScheduledJobNow();

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [hilEnabled, setHilEnabled] = useState(false);
  const [schedule, setSchedule] = useState<ScheduledJobBuilderState>(() => builderStateFromSpec(null));
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [jobStatus, setJobStatus] = useState<string>('active');

  const initialName = (job?.name ?? '').trim();
  const initialPrompt = useMemo(
    () => (job?.prompt ?? job?.description)?.trim() || '',
    [job?.prompt, job?.description]
  );
  const initialHilEnabled = job?.hilEnabled ?? false;
  const timezone =
    schedule.frequency === 'custom'
      ? job?.schedule?.timezone || SCHEDULE_DISPLAY_TIME_ZONE
      : SCHEDULE_DISPLAY_TIME_ZONE;

  useEffect(() => {
    if (!open || !job) return;
    setName(initialName);
    setPrompt(initialPrompt);
    setHilEnabled(job.hilEnabled ?? false);
    setSchedule(builderStateFromSpec(job.schedule, SCHEDULE_DISPLAY_TIME_ZONE));
    setScheduleDirty(false);
    setJobStatus(job.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.id]);

  const updateSchedule = (patch: Partial<ScheduledJobBuilderState>) => {
    setSchedule((prev) => ({ ...prev, ...patch }));
    setScheduleDirty(true);
  };

  const scheduleSpec = useMemo(() => buildScheduleSpec(schedule, timezone), [schedule, timezone]);

  // Debounce the live preview call so every keystroke doesn't fire a request.
  const [debouncedSpec, setDebouncedSpec] = useState(scheduleSpec);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSpec(scheduleSpec), 400);
    return () => clearTimeout(timer);
  }, [scheduleSpec]);

  const previewQuery = useSchedulePreview(scheduleDirty ? debouncedSpec : null);
  const runsQuery = useScheduledJobRuns(job?.id ?? '', { enabled: open && !!job });

  const previewInstant = parseIsoInstant(previewQuery.data?.occurrences?.[0]);
  const nextRunLabel = scheduleDirty
    ? previewInstant
      ? formatInstantLabel(previewInstant)
      : previewQuery.data?.humanReadable || (scheduleSpec ? '…' : 'Pick a valid schedule')
    : formatNextRunLabel(job);

  const trimmedName = name.trim();
  const trimmedPrompt = prompt.trim();
  const hasChanges =
    trimmedPrompt !== initialPrompt ||
    hilEnabled !== initialHilEnabled ||
    trimmedName !== initialName ||
    scheduleDirty;
  const scheduleValid = !scheduleDirty || scheduleSpec !== null;
  const canSave =
    !!job &&
    hasChanges &&
    trimmedPrompt.length > 0 &&
    trimmedName.length > 0 &&
    scheduleValid &&
    !updateMutation.isPending;

  const handleOpenChat = () => {
    if (!job) return;
    onOpenChat?.(job);
  };

  const handleSave = async () => {
    if (!job || !canSave) return;
    try {
      await updateMutation.mutateAsync({
        jobId: job.id,
        payload: {
          name: trimmedName !== initialName ? trimmedName : undefined,
          prompt: trimmedPrompt !== initialPrompt ? trimmedPrompt : undefined,
          hilEnabled: hilEnabled !== initialHilEnabled ? hilEnabled : undefined,
          schedule: scheduleDirty && scheduleSpec ? scheduleSpec : undefined,
        },
      });
      onPromptUpdated?.(job, trimmedPrompt, hilEnabled);
      setScheduleDirty(false);
      toast({
        title: t('SCHEDULED_JOB_UPDATED', { defaultValue: 'Scheduled job updated' }),
      });
    } catch (error) {
      console.error('Failed to update scheduled job:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_UPDATE_SCHEDULED_JOB', { defaultValue: 'Failed to update scheduled job' }),
        description: getApiErrorDetail(error) ?? t('PLEASE_TRY_AGAIN', { defaultValue: 'Please try again.' }),
      });
    }
  };

  const handlePause = async () => {
    if (!job) return;
    try {
      await pauseMutation.mutateAsync(job.id);
      setJobStatus('paused');
      toast({ title: t('SCHEDULED_JOB_PAUSED', { defaultValue: 'Job paused' }) });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_PAUSE_JOB', { defaultValue: 'Failed to pause job' }),
        description: getApiErrorDetail(error) ?? undefined,
      });
    }
  };

  const handleResume = async () => {
    if (!job) return;
    try {
      await resumeMutation.mutateAsync(job.id);
      setJobStatus('active');
      toast({ title: t('SCHEDULED_JOB_RESUMED', { defaultValue: 'Job resumed' }) });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_RESUME_JOB', { defaultValue: 'Failed to resume job' }),
        description: getApiErrorDetail(error) ?? undefined,
      });
    }
  };

  const handleRunNow = async () => {
    if (!job) return;
    try {
      await runNowMutation.mutateAsync(job.id);
      toast({ title: t('SCHEDULED_JOB_TRIGGERED', { defaultValue: 'Run started' }) });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_RUN_JOB', { defaultValue: 'Failed to start run' }),
        description: getApiErrorDetail(error) ?? undefined,
      });
    }
  };

  const showTime =
    schedule.frequency === 'daily' ||
    schedule.frequency === 'weekdays' ||
    schedule.frequency === 'weekly' ||
    schedule.frequency === 'monthly';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="flex min-h-0 max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-[565px] flex-col gap-0 overflow-hidden rounded-xl border-0 bg-true-white p-0 shadow-lg dark:bg-card sm:max-h-[min(90vh,900px)]"
      >
        <button
          type="button"
          aria-label={t('CLOSE', { defaultValue: 'Close' })}
          className="absolute right-2 top-2 z-20 inline-flex size-6 items-center justify-center text-grey-500 hover:text-grey-900 dark:text-muted-foreground dark:hover:text-foreground"
          onClick={() => onOpenChange(false)}
        >
          <Xmark width={24} height={24} strokeWidth={1.5} aria-hidden />
        </button>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-col gap-1 px-8 pt-8 pr-12">
            <p className="text-base font-normal leading-[1.6] text-link-accent">{nextRunLabel}</p>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle className="min-w-0 text-2xl font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                {job?.name || t('SCHEDULED_JOB', { defaultValue: 'Scheduled job' })}
              </DialogTitle>
              <div className="flex shrink-0 items-center gap-3 pt-1">
                <button
                  type="button"
                  aria-label={t('RUN_NOW', { defaultValue: 'Run now' })}
                  title={t('RUN_NOW', { defaultValue: 'Run now' })}
                  disabled={!job || job.running || runNowMutation.isPending}
                  onClick={() => void handleRunNow()}
                  className="inline-flex size-5 items-center justify-center text-grey-500 hover:text-grey-900 disabled:opacity-40 dark:text-muted-foreground dark:hover:text-foreground"
                >
                  <RefreshDouble width={20} height={20} strokeWidth={1.5} aria-hidden />
                </button>
                {jobStatus === 'paused' ? (
                  <button
                    type="button"
                    aria-label={t('RESUME', { defaultValue: 'Resume' })}
                    title={t('RESUME', { defaultValue: 'Resume' })}
                    disabled={resumeMutation.isPending}
                    onClick={() => void handleResume()}
                    className="inline-flex size-5 items-center justify-center text-grey-500 hover:text-grey-900 disabled:opacity-40 dark:text-muted-foreground dark:hover:text-foreground"
                  >
                    <Play width={20} height={20} strokeWidth={1.5} aria-hidden />
                  </button>
                ) : jobStatus === 'active' ? (
                  <button
                    type="button"
                    aria-label={t('PAUSE', { defaultValue: 'Pause' })}
                    title={t('PAUSE', { defaultValue: 'Pause' })}
                    disabled={pauseMutation.isPending}
                    onClick={() => void handlePause()}
                    className="inline-flex size-5 items-center justify-center text-grey-500 hover:text-grey-900 disabled:opacity-40 dark:text-muted-foreground dark:hover:text-foreground"
                  >
                    <Pause width={20} height={20} strokeWidth={1.5} aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-8 py-6">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="scheduled-job-name"
                className="text-sm font-medium leading-[1.6] text-grey-900 dark:text-foreground"
              >
                {t('NAME', { defaultValue: 'Name' })}
              </label>
              <Input
                id="scheduled-job-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('ENTER_NAME', { defaultValue: 'Enter name…' })}
                className="rounded-lg border-grey-100 bg-true-white text-base font-normal leading-[1.6] text-grey-900 shadow-none focus-visible:ring-1 dark:border-border dark:bg-card dark:text-foreground"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="scheduled-job-prompt"
                className="text-sm font-medium leading-[1.6] text-grey-900 dark:text-foreground"
              >
                {t('PROMPT', { defaultValue: 'Prompt' })}
              </label>
              <Textarea
                id="scheduled-job-prompt"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                rows={3}
                className="min-h-[56px] resize-none rounded-lg border border-grey-100 bg-true-white px-5 py-4 text-base font-normal leading-[1.6] text-grey-900 shadow-none focus-visible:ring-1 dark:border-border dark:bg-card dark:text-foreground"
                placeholder={t('ENTER_PROMPT', { defaultValue: 'Enter prompt…' })}
              />
            </div>

            <div className="flex w-full flex-col gap-3">
              <p className="text-sm font-medium leading-[1.6] text-grey-900 dark:text-foreground">
                {t('FREQUENCY', { defaultValue: 'Frequency' })}
              </p>

              <div className="flex w-full flex-col gap-4 rounded-md border border-grey-100 bg-grey-50 p-4 dark:border-border dark:bg-white/5">
                <FrequencyRow label={t('FREQUENCY', { defaultValue: 'Frequency' })}>
                  <KeyedSelectTrigger
                    ariaLabel={t('FREQUENCY', { defaultValue: 'Frequency' })}
                    value={schedule.frequency}
                    onValueChange={(value) => updateSchedule({ frequency: value })}
                    options={FREQUENCY_SELECT_OPTIONS}
                  />
                </FrequencyRow>

                {showTime ? (
                  <FrequencyRow label={t('TIME', { defaultValue: 'Time' })} showDivider>
                    <TimePickerTrigger
                      label={t('TIME', { defaultValue: 'Time' })}
                      value={schedule.timeOfDay}
                      onChange={(value) => updateSchedule({ timeOfDay: value })}
                    />
                  </FrequencyRow>
                ) : null}

                {schedule.frequency === 'weekly' ? (
                  <>
                    <div className="h-px w-full bg-grey-100 dark:bg-border" />
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="shrink-0 text-sm font-normal leading-[1.2] text-grey-900 dark:text-foreground">
                        {t('WEEKDAYS', { defaultValue: 'Weekdays' })}
                      </span>
                      <WeekdayToggleRow
                        weekdays={schedule.weekdays}
                        onToggle={(day) =>
                          updateSchedule({
                            weekdays: schedule.weekdays.includes(day)
                              ? schedule.weekdays.filter((d) => d !== day)
                              : [...schedule.weekdays, day],
                          })
                        }
                      />
                    </div>
                  </>
                ) : null}

                {schedule.frequency === 'monthly' ? (
                  <FrequencyRow label={t('DAY_OF_MONTH', { defaultValue: 'Day of month' })} showDivider>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={schedule.dayOfMonth}
                      onChange={(event) => {
                        const n = parseInt(event.target.value, 10);
                        updateSchedule({ dayOfMonth: Number.isFinite(n) ? n : 1 });
                      }}
                      className="h-8 w-20 border-0 bg-transparent text-right text-xs shadow-none focus-visible:ring-0"
                    />
                  </FrequencyRow>
                ) : null}

                {schedule.frequency === 'once' ? (
                  <FrequencyRow label={t('RUN_AT', { defaultValue: 'Run at' })} showDivider>
                    <DateTimePickerTrigger
                      label={t('RUN_AT', { defaultValue: 'Run at' })}
                      value={schedule.runAt}
                      onChange={(value) => updateSchedule({ runAt: value })}
                    />
                  </FrequencyRow>
                ) : null}

                {schedule.frequency === 'custom' ? (
                  <FrequencyRow label={t('CRON_EXPRESSION', { defaultValue: 'Cron expression' })} showDivider>
                    <Input
                      value={schedule.customCron}
                      onChange={(event) => updateSchedule({ customCron: event.target.value })}
                      placeholder="0 9 * * *"
                      className="h-8 border-0 bg-transparent text-right font-mono text-xs shadow-none focus-visible:ring-0"
                    />
                  </FrequencyRow>
                ) : null}
              </div>

              {schedule.frequency !== 'once' ? (
                <div className="flex w-full flex-col gap-4 rounded-md border border-grey-100 bg-grey-50 p-4 dark:border-border dark:bg-white/5">
                  <FrequencyRow label={t('END_REPEAT', { defaultValue: 'End Repeat' })}>
                    <KeyedSelectTrigger
                      ariaLabel={t('END_REPEAT', { defaultValue: 'End Repeat' })}
                      value={schedule.endRepeat}
                      onValueChange={(value) => updateSchedule({ endRepeat: value })}
                      options={END_REPEAT_SELECT_OPTIONS}
                    />
                  </FrequencyRow>
                  {schedule.endRepeat === 'onDate' ? (
                    <FrequencyRow label={t('END_DATE', { defaultValue: 'End date' })} showDivider>
                      <DatePickerTrigger
                        label={t('END_DATE', { defaultValue: 'End date' })}
                        value={schedule.endDate}
                        onChange={(value) => updateSchedule({ endDate: value })}
                      />
                    </FrequencyRow>
                  ) : null}
                </div>
              ) : null}

              <div className="flex w-full items-center justify-between gap-3 rounded-md border border-grey-100 bg-grey-50 p-4 dark:border-border dark:bg-white/5">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-sm font-normal leading-[1.2] text-grey-900 dark:text-foreground">
                    {t('REQUIRE_APPROVAL_FOR_ACTIONS', {
                      defaultValue: 'Require approval for actions',
                    })}
                  </span>
                  <span className="text-xs font-normal leading-[1.4] text-grey-500 dark:text-muted-foreground">
                    {t('REQUIRE_APPROVAL_FOR_ACTIONS_DESCRIPTION', {
                      defaultValue:
                        'When on, write actions pause for your approval; unanswered approvals expire and the run continues without them.',
                    })}
                  </span>
                </div>
                <Switch
                  checked={hilEnabled}
                  onCheckedChange={setHilEnabled}
                  aria-label={t('REQUIRE_APPROVAL_FOR_ACTIONS', {
                    defaultValue: 'Require approval for actions',
                  })}
                />
              </div>
            </div>

            {runsQuery.data?.items?.length ? (
              <div className="flex w-full flex-col gap-2">
                <p className="text-sm font-medium leading-[1.6] text-grey-900 dark:text-foreground">
                  {t('RECENT_RUNS', { defaultValue: 'Recent runs' })}
                </p>
                <div className="flex max-h-40 flex-col gap-2 overflow-y-auto rounded-md border border-grey-100 bg-grey-50 p-3 dark:border-border dark:bg-white/5">
                  {runsQuery.data.items.map((run) => (
                    <div key={run.id} className="flex items-center justify-between gap-3 text-xs">
                      <Badge variant={RUN_STATUS_VARIANT[run.status] ?? 'outline'} className="shrink-0">
                        {run.status}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate text-grey-500 dark:text-muted-foreground">
                        {run.errorMessage ||
                          formatRunTimestamp(run.finishedAt || run.startedAt || run.scheduledFor)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

        <div className="flex w-full shrink-0 flex-col gap-3 px-8 pb-8 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-[62px] border-grey-100 px-6 text-sm font-semibold leading-[1.2] text-grey-900 shadow-none dark:border-border dark:text-foreground sm:flex-1"
            onClick={() => void handleSave()}
            disabled={!canSave}
            loading={updateMutation.isPending}
          >
            {t('SAVE', { defaultValue: 'Save' })}
          </Button>
          <Button
            type="button"
            className="h-12 w-full rounded-[62px] bg-black-base px-6 text-sm font-semibold leading-[1.2] text-true-white shadow-none hover:bg-grey-900 dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white sm:flex-1"
            onClick={handleOpenChat}
            disabled={!job}
          >
            {t('OPEN_CHAT', { defaultValue: 'Open Chat' })}
          </Button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
